/**
 * Cells the pipeline grid and the Deals grid both draw, so the same fact
 * looks the same on both pages: they show the same rows at different
 * points, and should not read as two different products.
 *
 * The markup is what the hand-built tables used (dealmail, dealco, lbadge),
 * so the existing styles carry over untouched.
 */
import Icon from '../lib/icons.jsx';
import { daysAgo } from '../lib/dates.js';
import { Dash, SourceBadge, Pill } from './Primitives.jsx';

/** "3 days ago" / "today", for the date under a request - by the day,
    because that is all a request date knows. */
export function sinceDay(when) {
  const days = daysAgo(when);
  if (days === null) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/** The AI's likelihood to close, 0-100, or undefined where none was given. */
export const aiScoreOf = d => {
  if (d.aiScore === null || d.aiScore === undefined || d.aiScore === '') return undefined;
  const n = Number(d.aiScore);
  return Number.isFinite(n) ? n : undefined;
};

/** A likelihood to close as a coloured figure, with the reason on hover. */
export function ScorePill({ score, why }) {
  if (score === undefined) return <Dash />;
  const tone = score >= 67 ? 'p-green' : score >= 34 ? 'p-amber' : 'p-grey';
  return <Pill tone={tone} icon="zap" title={why || undefined}>{score}</Pill>;
}

/** An address is something you click, so it looks like it. */
export function ContactCell({ f }) {
  if (!f.email && !f.phone) return <Dash />;
  return (
    <div className="lcontact">
      {f.email ? (
        <a className="dealmail" href={`mailto:${f.email}`} title={f.email}>
          <Icon name="mail" />{f.email}
        </a>
      ) : null}
      {f.phone ? <span className="dealphone">{f.phone}</span> : null}
    </div>
  );
}

/** A company as a mark and a name, with where it is under it. */
export function CompanyCell({ company, place }) {
  if (!company) return <Dash />;
  return (
    <div className="dealco">
      <span className="ic"><Icon name="building" /></span>
      <div>
        <b title={company}>{company}</b>
        {place ? <small>{place}</small> : null}
      </div>
    </div>
  );
}

/** Where a row came from, and the campaign that brought it. */
export function SourceCell({ source, campaign }) {
  return (
    <div className="lcontact">
      <SourceBadge name={source} />
      {campaign ? <small className="lcamp" title={campaign}>{campaign}</small> : null}
    </div>
  );
}

/** An address on its own, for the Email column. */
export function EmailCell({ email }) {
  return email
    ? <a className="dealmail" href={`mailto:${email}`} title={email}><Icon name="mail" />{email}</a>
    : <Dash />;
}
